// ============================================================
// listing - what the Assets page's main pane shows for each view. Pure.
//
// A folder lists its own folders and assets; a search or a kind chip lists
// matching assets from EVERY folder; "Recently changed" the newest dozen;
// the Trash what was deleted, newest first.
// ============================================================
import { childrenOf } from './tree';
import type { AssetKind, AssetRow, FolderRow } from './types';

export type View =
  | { kind: 'folder'; folderId: string }
  | { kind: 'search'; query: string; assetKind: AssetKind | null }
  | { kind: 'recent' }
  | { kind: 'trash' }
  /** The open session's copies (the page opened from a session, v11.73.0). */
  | { kind: 'session' }
  /** Every team the library wears (plan teams-manager): a list of teams, not of assets. */
  | { kind: 'teams' };

export interface Listing {
  folders: FolderRow[];
  assets: AssetRow[];
}

/** How many "Recently changed" shows. */
export const RECENT_COUNT = 12;

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
const live = (assets: readonly AssetRow[]) => assets.filter((a) => !a.deletedAt);

/** Does an asset match a typed search? Its name or any tag, ignoring case. */
export function matches(asset: AssetRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  return !q || asset.name.toLowerCase().includes(q) || asset.tags.some((t) => t.toLowerCase().includes(q));
}

/** `copied`: the asset ids the open session holds a copy of (the session view lists those). */
export function listing(
  view: View,
  folders: readonly FolderRow[],
  assets: readonly AssetRow[],
  copied: ReadonlySet<string> = new Set(),
): Listing {
  switch (view.kind) {
    case 'folder':
      return {
        folders: childrenOf(folders, view.folderId),
        assets: live(assets)
          .filter((a) => a.folderId === view.folderId)
          .sort(byName),
      };
    case 'search':
      return {
        folders: [],
        assets: live(assets)
          .filter((a) => (!view.assetKind || a.kind === view.assetKind) && matches(a, view.query))
          .sort(byName),
      };
    case 'recent':
      return {
        folders: [],
        assets: live(assets)
          .sort((a, b) => b.updatedAt - a.updatedAt)
          .slice(0, RECENT_COUNT),
      };
    case 'session':
      return {
        folders: [],
        assets: live(assets)
          .filter((a) => copied.has(a.id))
          .sort(byName),
      };
    case 'trash':
      return { folders: [], assets: assets.filter((a) => a.deletedAt).sort((a, b) => b.deletedAt - a.deletedAt) };
    case 'teams':
      return { folders: [], assets: [] };
  }
}
